import request from "@/utils/request";
import { submitVideoGenerationRequest } from './create';
import { getToken } from "@/utils/auth";
import { getScriptCommentaryMode } from "@/utils/scriptCommentaryMode";

export function captureScriptVideoFrame(scriptId, sceneId, data) {
  return request({ url: `/api/c/scripts/${scriptId}/scenes/${sceneId}/video-frame`, method: 'post', data, timeout: 120000, silentError: true });
}

export function uploadVideoFrameImage(blob, frame) {
  const data = new FormData();
  data.append('file', blob, `video-frame-${frame}.jpg`);
  data.append('type', 'reference');
  return request({ url: '/api/upload', method: 'post', data, timeout: 120000, silentError: true, headers: { 'Content-Type': 'multipart/form-data' } });
}

export function getVideoToolMaterials(scriptId, params) {
  return request({ url: `/api/c/scripts/${scriptId}/video-tool-materials`, method: 'get', params, silentError: true });
}

export function prepareVideoToolMaterials(scriptId, data) {
  return request({ url: `/api/c/scripts/${scriptId}/video-tool-materials`, method: 'post', data, timeout: 360000, silentError: true });
}

export function getScripts(params) {
  return request({
    url: "/api/c/scripts",
    method: "get",
    params,
  });
}

export function createScript(data) {
  return request({
    url: "/api/c/scripts",
    method: "post",
    data,
  });
}

export function getScriptDetail(scriptId, options = {}) {
  return request({
    url: `/api/c/scripts/${scriptId}`,
    method: "get",
    silentError: !!options.silentError,
  });
}

export function getScriptVideoCosts(scriptId, params) {
  return request({
    url: `/api/c/scripts/${encodeURIComponent(scriptId)}/generation-cost/videos`,
    method: 'get',
    params,
    silentError: true,
  });
}

export function getScriptCostDetails(scriptId, category, params) {
  return request({
    url: `/api/c/scripts/${encodeURIComponent(scriptId)}/cost-details/${encodeURIComponent(category)}`,
    method: 'get',
    params,
    silentError: true,
  });
}

export function updateScript(scriptId, data) {
  return request({
    url: `/api/c/scripts/${scriptId}`,
    method: "put",
    data,
  });
}

export function getVideoReasoningSupplements(scriptId) {
  return request({ url: `/api/c/scripts/${scriptId}/video-reasoning-supplements`, method: 'get', silentError: true });
}

export function saveVideoReasoningSupplement(scriptId, data) {
  return request({ url: `/api/c/scripts/${scriptId}/video-reasoning-supplements`, method: 'put', data, silentError: true });
}

export function saveBackgroundMusic(scriptId, batchItemId, data) {
  return request({
    url: `/api/c/scripts/${scriptId}/batch-items/${batchItemId}/background-music`,
    method: 'put',
    data,
    silentError: true,
  });
}

// export function deleteScript(scriptId) {
//   return request({
//     url: `/api/c/scripts/${scriptId}`,
//     method: "delete",
//   });
// }

export function deleteScript(ids) {
  return request({
    url: `/api/c/scripts/batch-delete`,
    method: "post",
    data:{
      ids: ids
    }
  });
}

export function getScriptReasoningModels() {
  return request({
    url: "/api/c/scripts/reasoning/models",
    method: "get",
  });
}

export function reasonScript(data, requestConfig) {
  return request({
    url: "/api/c/scripts/reasoning",
    method: "post",
    data,
    timeout: 30 * 60 * 1000, // AI 推理需要更长时间，设为 30 分钟
    ...(requestConfig || {}),
  });
}

export function createScriptScene(scriptId, data) {
  return request({
    url: `/api/c/scripts/${scriptId}/scenes`,
    method: "post",
    data,
  });
}

export function batchCreateScenes(scriptId, data) {
  return request({
    url: `/api/c/scripts/${scriptId}/scenes/batch`,
    method: "post",
    data,
  });
}

export function addScenesBetween(scriptId, data) {
  return request({
    url: `/api/c/scripts/${scriptId}/scenes/add-scences`,
    method: "post",
    data,
  });
}

export function updateScriptScene(scriptId, sceneId, data, options = {}) {
  return request({
    url: `/api/c/scripts/${scriptId}/scenes/${sceneId}`,
    method: "put",
    data,
    silentError: !!options.silentError,
  });
}

export function replaceWan30Segments(scriptId, sceneId, segments) {
  return request({
    url: `/api/c/scripts/${scriptId}/scenes/${sceneId}/wan30-segments`,
    method: "put",
    data: { segments },
  });
}

/** 按 Seedance 2.0 实际返回数量替换当前集的视频提示词片段。 */
export function replaceSeedanceSegments(scriptId, sceneId, segments) {
  return request({
    url: `/api/c/scripts/${scriptId}/scenes/${sceneId}/seedance-segments`,
    method: "put",
    data: { segments },
  });
}

/** 全剧文本修补：preview=true 只预览；保存需要快照与二次确认。 */
export function repairScriptText(scriptId, data) {
  return request({
    url: `/api/c/scripts/${scriptId}/text-repair`,
    method: "post",
    data,
  });
}

export function previewShotPromptImport(scriptId, file, episode, targets) {
  const data = new FormData();
  data.append('file', file);
  data.append('episode', String(episode));
  data.append('targets', JSON.stringify(targets));
  return request({ url: `/api/c/scripts/${scriptId}/shot-prompt-import/preview`, method: 'post', data,
    headers: { 'Content-Type': 'multipart/form-data' } });
}

export function saveShotPromptImport(scriptId, data) {
  return request({ url: `/api/c/scripts/${scriptId}/shot-prompt-import/save`, method: 'post', data });
}

/** D 模板：在指定视频片段之间直接插入一个空白手动片段。 */
export function createDVideoSegment(scriptId, data) {
  return request({
    url: `/api/c/scripts/${scriptId}/d-video-segments`,
    method: "post",
    data,
  });
}

/** D 模板：删除手动新增的空白视频片段。 */
export function deleteDVideoSegment(scriptId, sceneId) {
  return request({
    url: `/api/c/scripts/${scriptId}/d-video-segments/${sceneId}`,
    method: "delete",
  });
}

export function createScriptCharacter(scriptId, data) {
  return request({
    url: `/api/c/scripts/${scriptId}/characters`,
    method: "post",
    data,
  });
}

export function batchCreateLocations(scriptId, data) {
  return request({
    url: `/api/c/scripts/${scriptId}/locations/batch`,
    method: "post",
    data,
  });
}

export function updateScriptCharacter(scriptId, characterId, data) {
  return request({
    url: `/api/c/scripts/${scriptId}/characters/${characterId}`,
    method: "put",
    data,
  });
}

export function createScriptCharacterForm(scriptId, characterId, data) {
  return request({
    url: `/api/c/scripts/${scriptId}/characters/${characterId}/forms`,
    method: "post",
    data,
  });
}

export function createScriptLocation(scriptId, data) {
  return request({
    url: `/api/c/scripts/${scriptId}/locations`,
    method: "post",
    data,
  });
}

export function createScriptLocationForm(scriptId, locationId, data) {
  return request({
    url: `/api/c/scripts/${scriptId}/locations/${locationId}/forms`,
    method: "post",
    data,
  });
}

export function updateScriptCharacterForm(scriptId, characterId, formId, data) {
  return request({
    url: `/api/c/scripts/${scriptId}/characters/${characterId}/forms/${formId}`,
    method: "put",
    data,
  });
}

export function updateScriptLocationForm(scriptId, locationId, formId, data) {
  return request({
    url: `/api/c/scripts/${scriptId}/locations/${locationId}/forms/${formId}`,
    method: "put",
    data,
  });
}

export function deleteScriptCharacterForm(scriptId, characterId, formId) {
  return request({
    url: `/api/c/scripts/${scriptId}/characters/${characterId}/forms/${formId}`,
    method: "delete",
  });
}

export function deleteScriptLocationForm(scriptId, locationId, formId) {
  return request({
    url: `/api/c/scripts/${scriptId}/locations/${locationId}/forms/${formId}`,
    method: "delete",
  });
}

export function deleteScriptCharacter(scriptId, characterId) {
  return request({
    url: `/api/c/scripts/${scriptId}/characters/${characterId}`,
    method: "delete",
  });
}

export function deleteScriptLocation(scriptId, locationId) {
  return request({
    url: `/api/c/scripts/${scriptId}/locations/${locationId}`,
    method: "delete",
  });
}

export function deleteScriptProp(scriptId, propId) {
  return request({
    url: `/api/c/scripts/${scriptId}/props/${propId}`,
    method: "delete",
  });
}

export function upsertScriptNarrator(scriptId, data) {
  return request({
    url: `/api/c/scripts/${scriptId}/narrator`,
    method: "put",
    data,
  });
}

export function updateScriptLocation(scriptId, locationId, data) {
  return request({
    url: `/api/c/scripts/${scriptId}/locations/${locationId}`,
    method: "put",
    data,
  });
}

export function batchGenerateSceneVideos(scriptId, data) {
  return request({
    url: `/api/c/scripts/${scriptId}/scenes/batch-generate`,
    method: "post",
    data,
    timeout: 10 * 60 * 1000, // AI 生成需要更长时间
  });
}

export function deleteScriptScene(scriptId, sceneId) {
  return request({
    url: `/api/c/scripts/${scriptId}/scenes/${sceneId}`,
    method: "delete",
  });
}

export function addSceneToLibrary(scriptId, locationId, data) {
  return request({
    url: `/api/c/scripts/${scriptId}/assets/locations/${locationId}`,
    method: "post",
    data,
  });
}
//获取分镜图素材id
export function uploadSceneAssets(scriptId, sceneId, data) {
  return request({
    url: `/api/c/scripts/${scriptId}/assets/scenes/${sceneId}`,
    method: "post",
    data,
  });
}

/**
 * 清空整部剧的 seedance_assets_id（6张表）
 */
export function clearSeedanceAssetsId(scriptId) {
  return request({
    url: `/api/c/scripts/${scriptId}/clear-seedance-assets-id`,
    method: "post",
  });
}

/**
 * 获取素材库全量资产（角色+场景），用于"从库中添加"弹窗
 * 返回的数据应包含当前项目以及全局素材库的角色/场景
 */
export function getLibraryAssets(scriptId) {
  return request({
    url: `/api/c/scripts/${scriptId}/assets`,
    method: "get",
  });
}


//资产库项目列表
export function getListOptions(params) {
  return request({
    url: "/api/c/scripts/listOptions",
    method: "get",
    params,
  });
}
//资产库剧本图片列表
export function getImageOptions(scriptId, params) {
  return request({
    url: `/api/c/scripts/${scriptId}/images`,
    method: "get",
    params,
  });
}


//获取用户列表
export function getUserList() {
  return request({
    url: `/api/c/auth/userList`,
    method: "get",
  });
}

// 修改素材图片名称
export function updateImageName(scriptId, imageId, data) {
  return request({
    url: `/api/c/scripts/${scriptId}/images/${imageId}/name`,
    method: "post",
    data,
  });
}

/**
 * 资产智能匹配
 * @param {Object} data - { segmentId, scriptId, types }
 */
export function matchAssets(data) {
  return request({
    url: "/api/c/scripts/match-assets",
    method: "post",
    data,
  });
}

// ============ 分批推理任务（后端化） ============

export function createBatchTask(data) {
  if (getScriptCommentaryMode(data) === 2) {
    return Promise.reject(new Error('社媒剧由外部 APP 创建和制作'));
  }
  return request({ url: '/api/c/scripts/batch-task', method: 'post', data });
}

export function getBatchTaskByScript(scriptId) {
  return request({ url: `/api/c/scripts/batch-task/by-script/${scriptId}`, method: 'get' });
}

export function getBatchTask(taskId) {
  return request({ url: `/api/c/scripts/batch-task/${taskId}`, method: 'get' });
}

export function retryBatchTaskItem(taskId, batchIndex) {
  return request({ url: `/api/c/scripts/batch-task/${taskId}/items/${batchIndex}/retry`, method: 'post' });
}

export function retryBatchTaskRoster(taskId) {
  return request({ url: `/api/c/scripts/batch-task/${taskId}/retry-roster`, method: 'post' });
}

/**
 * 启动剩余批次（确认分镜后触发后续分批推理）
 * POST /api/c/scripts/batch-task/:taskId/start-remaining
 * @param {string} taskId script_batch_tasks.task_id（前缀 bgt_）
 */
export function startRemainingBatchTask(taskId) {
  return request({ url: `/api/c/scripts/batch-task/${taskId}/start-remaining`, method: 'post' });
}

export function deleteBatchTask(taskId) {
  return request({ url: `/api/c/scripts/batch-task/${taskId}`, method: 'delete' });
}

// ===== 手动集数管理（Episode Management）=====

/**
 * 手动添加一集
 * POST /api/c/scripts/batch-task/episodes
 * @param {Object} data { scriptId, title? }
 */
export function addEpisode(data) {
  return request({ url: '/api/c/scripts/batch-task/episodes', method: 'post', data });
}

/**
 * 名册提取完成后回写（前端完成 extract+dedup 大模型调用后调用）
 * @param {Object} data
 * @param {number}  data.scriptId        必填，剧本 id
 * @param {number} [data.taskId]         可选，批处理任务 id；不传则后端取最新一条
 * @param {string}  data.rosterText      必填，最终去重后的名册文本（102 返回结果）
 * @param {string} [data.firstSegmentRaw] 可选，extract 第一段原始返回（含 projectSummary）
 */
export function saveRosterResult(data) {
  return request({
    url: '/api/c/scripts/roster-save',
    method: 'post',
    data,
  });
}

/**
 * 单批 extract 成功后回写 description（含 projectSummary 时同步更新 scripts.description）
 * @param {Object} data
 * @param {number} data.scriptId        必填，剧本 id
 * @param {string} data.firstSegmentRaw 必填，extract 第一段原始返回
 */
export function saveScriptDescription(data) {
  return request({
    url: '/api/c/scripts/description-save',
    method: 'post',
    data,
  });
}

/**
 * 基于已存名册的二次推理（取 script_batch_tasks.roster_text 作为输入）
 * @param {Object} data
 * @param {number}  data.modelId   必填，ai_models.id
 * @param {number}  data.scriptId  必填
 * @param {number} [data.taskId]   可选，批处理任务 id
 */
export function reasoningWithRoster(data) {
  return request({
    url: '/api/c/scripts/with-roster',
    method: 'post',
    data,
    timeout: 30 * 60 * 1000,
  });
}

// 批量删除素材图片
export function batchDeleteImages(scriptId, data) {
  return request({
    url: `/api/c/scripts/${scriptId}/images/batchDelete`,
    method: "post",
    data,
  });
}

// 切换素材收藏状态
export function toggleFavorite(data) {
  return request({
    url: '/api/c/assets/toggle-favorite',
    method: 'post',
    data,
  });
}

// 获取公司收藏资产列表
export function getCompanyAssetList(params) {
  return request({
    url: '/api/c/assets/list',
    method: 'get',
    params,
  });
}

// 批量取消收藏公司资产
export function batchUnfavorite(ids) {
  return request({
    url: '/api/c/assets/batch-unfavorite',
    method: 'post',
    data: { ids },
  });
}

/**
 * 提交视频合成任务
 * @param {Object} data - { scriptId, episodeIndex, segmentIds }
 */
export function composeVideo(data) {
  return request({
    url: "/api/c/scripts/compose",
    method: "post",
    data,
  });
}

// 上传素材图片
export function uploadImage(scriptId, data) {
  return request({
    url: `/api/c/scripts/${scriptId}/images/upload`,
    method: "post",
    data,
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 120000,
  });
}

// 上传文件到腾讯云COS
export function uploadToQcloud(data) {
  return request({
    url: '/api/upload/qcloud',
    method: 'post',
    data,
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  });
}

/**
 * 查询视频合成任务状态
 * @param {string} taskId - 合成任务 ID
 */
export function getComposeStatus(taskId) {
  return request({
    url: `/api/c/scripts/compose/${taskId}`,
    method: "get",
  });
}

/**
 * 获取历史合成记录
 * @param {number} scriptId
 * @param {number} episodeIndex
 */
export function getComposeHistory(scriptId, episodeIndex) {
  return request({
    url: "/api/c/scripts/compose/history",
    method: "get",
    params: { scriptId, episodeIndex },
  });
}

/**
 * 获取七牛云直传凭证
 * @param {string} filename - 原始文件名
 * @param {string} type     - 文件类型，默认 'compose'
 */
export function getUploadToken(filename, type = 'compose') {
  return request({
    url: `/api/upload/token`,
    method: "get",
    params: { filename, type },
  });
}

/**
 * 保存前端合成结果到后端
 * @param {Object} data - { scriptId, episodeIndex, segmentIds, videoUrl, fileSize }
 */
export function saveComposeResult(data) {
  return request({
    url: "/api/c/scripts/compose/save",
    method: "post",
    data,
  });
}

/**
 * 保存 103 推理产出的角色/场景/道具/分镜
 * POST /api/c/scripts/save-storyboard
 * @param {Object} data
 * @param {string} data.content  - 103 模型返回的原始文本
 * @param {number} data.taskId   - script_batch_tasks.id
 *
 * silentError: 失败时不弹全局顶部错误提示。
 *   - 副线在「角色」阶段后台触发，顶部弹窗会污染角色页
 *   - 「分镜」阶段已有 storyboard-error 占位 + 「重新推理」按钮兜底
 *   失败信息由调用方根据 storyboardReasoningStatus 在组件内呈现。
 */
export function saveStoryboard(data) {
  return request({
    url: '/api/c/scripts/save-storyboard',
    method: 'post',
    data,
    silentError: true,
  });
}

/**
 * 保存 151 推理内容：解析角色清单 / 场景清单并落库 + 留存 script_reasoning_records
 * POST /api/c/scripts/save-reasoning
 * @param {Object} data
 * @param {number} data.script_id     - 剧本 id（后端按 snake_case 接收）
 * @param {number} [data.model_id]    - 推理模型 id（例如 151）
 * @param {string} [data.input_content] - 推理输入原文（可选，用于溯源；后端截取前 20000 字）
 * @param {string} data.content       - 151 接口返回的原始 JSON 文本（必填）
 */
export function saveReasoning(data) {
  return request({
    url: '/api/c/scripts/save-reasoning',
    method: 'post',
    data,
  });
}

/**
 * 保存剧本简介（modelId=152 推理成功后调用，把原始结果落库到 scripts.story_synopsis）
 * @param {Object} data
 * @param {number} data.scriptId - 剧本 id
 * @param {string} data.content  - 152 接口返回的原始文本（剧本简介+分集）
 */
export function saveStorySynopsis(data) {
  return request({
    url: '/api/c/scripts/story-synopsis-save',
    method: 'post',
    data,
  });
}

export function updateStorySynopsis(scriptId, data) {
  return request({
    url: `/api/c/scripts/${scriptId}/story-synopsis`,
    method: 'put',
    data,
    silentError: true,
  });
}

/**
 * 保存本集色调（写入 script_batch_items 表）
 * @param {Object} data
 * @param {number} data.scriptId          - 剧本 id
 * @param {number} data.batchItemId       - 当前分集 id（script_batch_items.id）
 * @param {string} data.colorToneText     - 色调描述文本
 * @param {string} [data.colorToneImageUrl] - 色调参考图 URL
 */
export function saveColorTone(data) {
  return request({
    url: '/api/c/scripts/color-tone-save',
    method: 'post',
    data,
  });
}

/**
 * 获取某剧本所有分批的 input_text 拼接后的完整文本
 * 用于刷新恢复 152 推理：刷新后 sessionStorage 草稿丢失，但分批已写库，
 * 拼接后即可还原 152 的输入，从而继续推理。
 * @param {number|string} scriptId - 剧本 id
 */
export function concatBatchInputText(scriptId) {
  return request({
    url: '/api/c/scripts/batch-task/concat-input-text',
    method: 'get',
    params: { scriptId },
  });
}

/**
 * 更新角色图片
 * @param {Object} data
 * @param {number|string} data.id        - 角色 id（is_default=1）或子图 id（is_default=0）
 * @param {string}        data.img_url   - 新图片链接
 * @param {0|1}           data.is_default - 1=默认图(script_characters)，0=子图(script_character_forms)
 */
export function updateCharacterImage(data) {
  return request({
    url: "/api/c/scripts/character/update-image",
    method: "post",
    data,
  });
}


/**
 * 解析 158 模型返回的文本，转换为编辑表单字段
 * @param {string} content - 158 原始返回文本
 * @param {string} [type] - 解析类型：'character'（默认）| 'scene' | 'prop'
 */
export function parseCharacterContent(content, type) {
  return request({
    url: '/api/c/scripts/parse-character-content',
    method: 'post',
    data: { content, ...(type ? { type } : {}) },
  });
}

// 生成封面图
export function generateCoverImage(scriptId, data) {
  return request({
    url: `/api/c/scripts/${scriptId}/cover-image`,
    method: 'post',
    data,
  });
}

/**
 * 克隆项目（深度复制所有关联数据）
 * POST /api/c/scripts/:id/clone
 */
export function cloneScript(scriptId) {
  return request({
    url: `/api/c/scripts/${scriptId}/clone`,
    method: 'post',
  });
}

export function getVideoHistory(params) {
  return request({
    url: '/api/c/scripts/video-history',
    method: 'get',
    params,
  });
}

// ============ 资产库自定义项目 ============

// 获取自定义项目列表
export function getAssetProjects(params) {
  return request({
    url: '/api/c/asset-projects',
    method: 'get',
    params,
  });
}

// 资产库项目归类（与图片标签分开）
export function getAssetProjectTypeOptions() {
  return request({ url: '/api/c/asset-projects/type-options', method: 'get' });
}

export function updateAssetProjectTypes(type, id, projectTypes) {
  return request({
    url: `/api/c/asset-projects/types/${type}/${id}`,
    method: 'put',
    data: { projectTypes },
  });
}

// 创建自定义资产项目
export function createAssetProject(data) {
  return request({
    url: '/api/c/asset-projects',
    method: 'post',
    data,
  });
}

// 更新自定义资产项目（编辑名称/封面）
export function updateAssetProject(id, data) {
  return request({
    url: `/api/c/asset-projects/${id}`,
    method: 'put',
    data,
  });
}

// 置顶/取消置顶资产库项目（type: custom | script）
export function updateAssetProjectPin(type, id, pinned) {
  return request({
    url: `/api/c/asset-projects/pin/${type}/${id}`,
    method: 'put',
    data: { pinned },
  });
}

// 上传封面图到自定义项目
export function uploadAssetProjectCover(data) {
  return request({
    url: '/api/c/asset-projects/upload-cover',
    method: 'post',
    data,
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  });
}

// 删除自定义资产项目
export function deleteAssetProject(id) {
  return request({
    url: `/api/c/asset-projects/${id}`,
    method: 'delete',
  });
}

// 获取自定义项目图片列表（支持可选分页参数）
export function getAssetProjectImages(id, params) {
  return request({
    url: `/api/c/asset-projects/${id}/images`,
    method: 'get',
    params,
  });
}

// 合并多个自定义项目到一个新项目
export function mergeAssetProjects(data) {
  return request({
    url: '/api/c/asset-projects/merge',
    method: 'post',
    data,
  });
}

// 上传图片到自定义项目
export function uploadAssetProjectImage(id, data) {
  return request({
    url: `/api/c/asset-projects/${id}/images/upload`,
    method: 'post',
    data,
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  });
}

// 删除自定义项目中的图片
export function deleteAssetProjectImage(id, imgId) {
  return request({
    url: `/api/c/asset-projects/${id}/images/${imgId}`,
    method: 'delete',
  });
}

// 修改自定义项目图片名称
export function updateAssetProjectImageName(id, imgId, data) {
  return request({
    url: `/api/c/asset-projects/${id}/images/${imgId}/name`,
    method: 'post',
    data,
  });
}

// 获取所有自定义项目的图片（分页）
export function getAssetProjectAllImages(params) {
  return request({
    url: '/api/c/asset-projects/all-images',
    method: 'get',
    params,
  });
}

export async function reasonScriptStreamBySSE(data, options) {
  const { onProgress } = options || {};
  const baseURL = typeof request.getBaseURL === "function" ? request.getBaseURL() : "";
  const token = getToken();
  const normalizedBaseURL = String(baseURL || "").replace(/\/+$/, "");
  const targetUrl = normalizedBaseURL + "/api/c/scripts/reasoning-stream";
  const response = await fetch(targetUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      ...(token ? { Authorization: "Bearer " + token } : {}),
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) throw new Error("推理请求失败（" + response.status + "）");
  if (!response.body) throw new Error("当前浏览器不支持流式响应");

  const reader = response.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";
  let resultText = "";
  let finalPayload = null;
  let completed = false;

  const handleEventBlock = (block) => {
    const eventLine = block.split(/\r?\n/).find(line => line.startsWith("event:"));
    const event = eventLine ? eventLine.slice(6).trim() : "message";
    const dataLines = block
      .split(/\r?\n/)
      .filter((line) => line.startsWith("data:"))
      .map((line) => line.replace(/^data:\s?/, ""));
    if (!dataLines.length) return false;

    const raw = dataLines.join("\n").trim();
    if (!raw) return false;
    if (raw === "[DONE]") { completed = true; return true; }

    let payload = raw;
    try {
      payload = JSON.parse(raw);
    } catch (e) { }

    if (payload && typeof payload === "object") {
      if (event === "error" || (payload.code != null && Number(payload.code) !== 0)) {
        throw new Error(payload.message || "推理失败");
      }
      if (["connected", "ping", "heartbeat"].includes(event)) return false;
      if (event === "think") {
        if (typeof onProgress === "function") onProgress(resultText, payload);
        return false;
      }
      if (!["message", "delta", "done"].includes(event)) return false;
      const chunk =
        payload.delta ||
        payload.content ||
        payload.text ||
        payload.result ||
        (payload.data && (payload.data.delta || payload.data.content || payload.data.text || payload.data.result)) ||
        "";

      if (chunk) {
        if (payload.result || (payload.data && payload.data.result)) {
          resultText = chunk;
        } else {
          resultText += chunk;
        }
        if (typeof onProgress === "function") onProgress(resultText, payload);
      }

      finalPayload = payload;
      if (event === "done") {
        if (!chunk) throw new Error("推理返回为空，未自动重复提交");
        completed = true;
      }
      return completed;
    }

    resultText += String(payload);
    if (typeof onProgress === "function") onProgress(resultText, payload);
    return false;
  };

  try {
    while (!completed) {
      const { done, value } = await reader.read();
      buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
      const blocks = buffer.split(/\r?\n\r?\n/);
      buffer = blocks.pop() || "";
      for (const block of blocks) {
        if (handleEventBlock(block)) break;
      }
      if (done) break;
    }
    if (!completed && buffer.trim()) handleEventBlock(buffer);
    if (!completed) throw new Error("推理连接中断，尚未收到完整结果；未自动重复提交，请先检查生成历史");
  } finally {
    try { await reader.cancel(); } catch (e) { /* 流可能已经关闭 */ }
    reader.releaseLock();
  }
  if (!resultText) throw new Error("推理返回为空，请稍后重试");

  return {
    code: 0,
    data: {
      ...(finalPayload && typeof finalPayload === "object" ? (finalPayload.data || finalPayload) : {}),
      result: resultText,
    },
  };
}

// ===== 导演台姿态预设 =====

/** 获取该剧本下所有命名预设 */
export function getDirectorPoses(scriptId) {
  return request({
    url: "/api/c/director-poses/",
    method: "get",
    params: { scriptId },
  });
}

/** 保存/更新命名预设 */
export function saveDirectorPose(data) {
  return request({
    url: "/api/c/director-poses/",
    method: "post",
    data,
  });
}

/** 删除指定预设 */
export function deleteDirectorPose(id) {
  return request({
    url: `/api/c/director-poses/${id}`,
    method: "delete",
  });
}

/** 获取分镜级自动保存的姿态 */
export function getScenePose(scriptId, sceneId) {
  return request({
    url: "/api/c/director-poses/scene",
    method: "get",
    params: { scriptId, sceneId },
  });
}

/** 保存分镜级自动保存的姿态 */
export function saveScenePose(data) {
  return request({
    url: "/api/c/director-poses/scene",
    method: "post",
    data,
  });
}

/** 通用文件上传（用于导演台 base64 → Blob 上传等场景） */
export function uploadFile(data) {
  return request({
    url: '/api/upload',
    method: 'post',
    data,
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  });
}

// ============ 协作人管理 ============

/** 获取剧本协作人列表 */
export function getCollaborators(scriptId) {
  return request({
    url: `/api/c/scripts/${scriptId}/collaborators`,
    method: 'get',
  });
}

/** 获取可添加的协作人候选列表（基于剧本 owner 同 team，owner/admin 可调） */
export function getCollaboratorCandidates(scriptId) {
  return request({
    url: `/api/c/scripts/${scriptId}/collaborator-candidates`,
    method: 'get',
  });
}

/** 添加协作人（批量） */
export function addCollaborators(scriptId, data) {
  return request({
    url: `/api/c/scripts/${scriptId}/collaborators`,
    method: 'post',
    data,
  });
}

/** 移除协作人 */
export function removeCollaborator(scriptId, userId) {
  return request({
    url: `/api/c/scripts/${scriptId}/collaborators/${userId}`,
    method: 'delete',
  });
}

/** 视频画质增强 */
export function enhanceVideo(data) {
  return submitVideoGenerationRequest('/api/c/scripts/enhance-video', data);
}
